import { useTranslation } from 'next-i18next'
import { useMemo, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import Times from '~/bootcamp/Activities/components/Times'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { NumHistory } from '~/bootcamp/Activities/Multiplication/MultiBy/types'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

interface Props extends ActivityProps {
  amount: number
  minNumber: number
  maxNumber: number
}

export default function MultiplyFactors({
  amount,
  minNumber,
  maxNumber,
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  difficultyLevel,
  onNextQuestionClick,
  actionButtonsFC,
  autofocus = true,
}: Props): React.ReactElement {
  const { t } = useTranslation('generics')
  const [answer, setAnswer] = useState('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [taskHistory, setTaskHistory] = useState<NumHistory[]>([])
  const historyNumbers = useMemo(() => taskHistory.map(({ num }) => num), [taskHistory])
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const numbers = useMemo(() => {
    if (difficultyLevel) return generateDigits(minNumber, maxNumber, amount, difficultyLevel, historyNumbers)
  }, [historyNumbers, difficultyLevel, amount, minNumber, maxNumber])

  useEmitInstance(onInstanceChange, numbers ? `${numbers.join(' \\cdot ')} = \\square` : null)

  const handleOnCheckAnswerClick = (): void => {
    let correctAnswer = 0
    numbers?.map((num) => (correctAnswer ? (correctAnswer = num * correctAnswer) : (correctAnswer = num)))
    onAnswerStateChange(parseFloat(answer) === correctAnswer)
  }

  const handleOnNextQuestionClick = (): void => {
    onAnswerStateChange(null)
    setAnswer('')
    if (numbers) {
      const num = numbers[0]
      setTaskHistory((prevState) => {
        return [{ num }, ...prevState].slice(0, 3)
      })
    }
    onNextQuestionClick()
    setInputKey(uuid())
  }
  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper className="py-5">
      <MultiplicationActivityWrapper>
        {numbers &&
          numbers.map((num, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={`${num}-${i}`}>
              {num.toString()}
              {numbers.length > i + 1 ? <Times /> : <></>}
            </span>
          ))}
        <div>
          <Equals />
          <Input
            aria-label={t('ariaLabelButtons.insertProduct')}
            autoFocus={autofocus}
            isError={answerState === false}
            isWide
            key={inputKey}
            maxDigits={15}
            onChange={(value) => {
              setAnswer(value)
              onAnswerStateChange(null)
            }}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer}
          />
        </div>
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

function generateDigits(
  min: number,
  max: number,
  amount: number,
  difficultyLevel: number,
  historyNumbers: number[],
): number[] {
  const numbers: number[] = [...Array(amount)].map(() => {
    return (
      // For difficulty level 1 we need the generated numbers to be between 1 and 5 or 10
      difficultyLevel === 1
        ? // incase the max or min changes 10 will still be as likely to appear as the other numbers
          Math.random() > 1 / (max - min + 2)
          ? Math.floor(Math.random() * (max - min + 1) + min)
          : 10
        : Math.floor(Math.random() * (max - min + 1) + min)
    )
  })

  if (historyNumbers.includes(numbers[0])) {
    return generateDigits(min, max, amount, difficultyLevel, historyNumbers)
  }

  return numbers
}
